import moment from 'moment'
import { ANCHOR_TYPE, FIXED_DATE_MODE } from '../constants'
import { hidesOffset, isNegativeOffsetDisallowed, offsetDays, selectedInstanceIds } from '../hooks/anchorConstraints'

/** Deploy anchors that require a selected study entity (matches AnchorEditor needsInstance). */
const ENTITY_INSTANCE_ANCHOR_TYPES = [
  ANCHOR_TYPE.arm,
  ANCHOR_TYPE.phase,
  ANCHOR_TYPE.visitCycle,
  ANCHOR_TYPE.track,
  ANCHOR_TYPE.chapter,
]

const entityInstanceRequiredMessage = type => {
  switch (type) {
    case ANCHOR_TYPE.arm:
      return 'Select an arm.'
    case ANCHOR_TYPE.phase:
      return 'Select a phase.'
    case ANCHOR_TYPE.visitCycle:
      return 'Select a cycle.'
    case ANCHOR_TYPE.track:
      return 'Select a track.'
    case ANCHOR_TYPE.chapter:
      return 'Select a chapter.'
    default:
      return 'Select the entity to anchor to.'
  }
}

const hasEntityInstance = anchor => Boolean(String(anchor?.instanceId ?? '').trim())

export const validateAnchorOffset = (anchor, pathPrefix) => {
  const errors = {}
  if (hidesOffset(anchor?.type)) return errors
  if (isNegativeOffsetDisallowed(anchor) && offsetDays(anchor) < 0) {
    if (anchor.type === ANCHOR_TYPE.icfSignatureDate) {
      errors[`${pathPrefix}.offset`] = 'ICF Signature Date offset must be 0 or greater.'
    } else {
      errors[`${pathPrefix}.offset`] =
        'Negative offset is not allowed for this end/completion event because the date is not known in advance.'
    }
  }
  return errors
}

export const validateAnchors = (schedule, scheduleIndex) => {
  const errors = {}
  const anchors = schedule?.anchors || []
  if (!anchors.length) {
    errors[`schedules.${scheduleIndex}.anchors`] = 'At least one anchor is required.'
    return errors
  }
  anchors.forEach((anchor, index) => {
    const prefix = `schedules.${scheduleIndex}.anchors.${index}`
    if (!anchor.type) {
      errors[`${prefix}.type`] = 'Select an anchor type.'
    }
    Object.assign(errors, validateAnchorOffset(anchor, prefix))
    if (anchor.type === ANCHOR_TYPE.visit && !anchor.visitStatus) {
      errors[`${prefix}.visitStatus`] = 'Select a visit status.'
    }
    if (anchor.type === ANCHOR_TYPE.instrument && !anchor.instrumentEvent) {
      errors[`${prefix}.instrumentEvent`] = 'Select Available, Completion, or Missed.'
    }
    if (anchor.type === ANCHOR_TYPE.instrument && !anchor.instanceId) {
      errors[`${prefix}.instanceId`] = 'Select the referenced instrument.'
    }
    if (anchor.type === ANCHOR_TYPE.visit && !selectedInstanceIds(anchor).length) {
      errors[`${prefix}.instanceId`] = 'Select at least one visit.'
    }
    if (ENTITY_INSTANCE_ANCHOR_TYPES.includes(anchor.type) && !hasEntityInstance(anchor)) {
      errors[`${prefix}.instanceId`] = entityInstanceRequiredMessage(anchor.type)
    }
    if (anchor.type === ANCHOR_TYPE.fixedCalendarDate) {
      const mode = anchor.fixedDate?.mode || FIXED_DATE_MODE.day
      const value = anchor.fixedDate?.value
      if (value == null || value === '') {
        errors[`${prefix}.fixedDate`] = 'Enter a calendar date value.'
      } else if (mode === FIXED_DATE_MODE.dayOfYear) {
        const day = Number(value)
        if (!Number.isInteger(day) || day < 1 || day > 366) {
          errors[`${prefix}.fixedDate`] = 'Enter a day of year between 1 and 366.'
        }
      } else if (mode === FIXED_DATE_MODE.weekOfYear) {
        const week = Number(value)
        if (!Number.isInteger(week) || week < 1 || week > 53) {
          errors[`${prefix}.fixedDate`] = 'Enter a week of year between 1 and 53.'
        }
      } else if (mode === FIXED_DATE_MODE.day) {
        const parsed = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true)
        if (!parsed.isValid()) {
          errors[`${prefix}.fixedDate`] = 'Enter a valid calendar date.'
        } else if (parsed.startOf('day').isBefore(moment().startOf('day'))) {
          errors[`${prefix}.fixedDate`] = 'Select today or a future date.'
        }
      }
    }
  })
  return errors
}
